import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, UserIcon, ClockIcon, TagIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  read_time: string;
  tags: string[];
  slug: string;
  published_at: string;
  featured_image?: string;
}

const Blog: React.FC = () => {
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchBlogPosts();
  }, []);

  const fetchBlogPosts = async () => {
    try {
      const response = await fetch('https://plencoplumbingltd.com/api/blogs');
      if (!response.ok) throw new Error('Failed to fetch blog posts');
      const data = await response.json();
      setBlogPosts(data.data || data);
      setError(null);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError('Failed to load blog posts');
      setBlogPosts([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white">
      <SEO 
        title="Blog - Industry Insights & Expert Guides | Plenco Plumbing & Construction"
        description="Read expert articles on construction, plumbing, and engineering from Plenco's professional team. Get technical guides, maintenance tips, industry updates, and best practices for construction projects in Uganda and East Africa."
        keywords="plumbing blog Uganda, construction tips Kampala, building maintenance guides, tank calibration information, plumbing advice East Africa, construction industry news Uganda, engineering tips"
        canonicalUrl="https://www.plencoplumbingltd.com/blog"
        type="website"
      />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-primary-700 text-white pt-32 sm:pt-36 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-black mb-4">
            <span className="text-white">Industry</span>{' '}
            <span className="text-primary-200">Insights</span>
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-primary-100 max-w-3xl mx-auto">
            Expert advice, technical guides, and industry updates from Uganda's leading construction, plumbing, and engineering specialists
          </p>
        </div>
      </section>

      {/* Blog Posts Grid - 3 columns */}
      <section className="py-12 sm:py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">Loading blog posts...</p>
            </div>
          )}

          {error && (
            <div className="text-center py-12 bg-red-50 border-l-4 border-red-500 p-4 rounded">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {!loading && !error && blogPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No blog posts available yet.</p>
            </div>
          )}

          {!loading && !error && blogPosts.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {blogPosts.map((post) => (
                <article
                  key={post.id}
                  className="bg-white rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group flex flex-col"
                  itemScope
                  itemType="https://schema.org/BlogPosting"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={post.featured_image || 'https://via.placeholder.com/600x400/035e85/ffffff?text=' + encodeURIComponent(post.title)}
                      alt={post.title}
                      itemProp="image"
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/600x400/035e85/ffffff?text=' + encodeURIComponent(post.title);
                      }}
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-bold shadow-lg" itemProp="articleSection">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3 flex-wrap gap-2">
                      <div className="flex items-center space-x-1">
                        <CalendarIcon className="h-4 w-4" />
                        <time dateTime={post.published_at} itemProp="datePublished">
                          {new Date(post.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </time>
                      </div>
                      <div className="flex items-center space-x-1">
                        <ClockIcon className="h-4 w-4" />
                        <span>{post.read_time}</span>
                      </div>
                    </div>
                    <h2 className="text-xl font-black text-gray-900 mb-3 group-hover:text-primary transition-colors leading-tight" itemProp="headline">
                      {post.title}
                    </h2>
                    <p className="text-gray-600 mb-4 leading-relaxed flex-1 text-sm" itemProp="description">
                      {post.excerpt}
                    </p>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <UserIcon className="h-4 w-4" />
                        <span itemProp="author">{post.author}</span>
                      </div>
                      {post.tags && (() => {
                        const tagsArray = Array.isArray(post.tags) ? post.tags : (typeof post.tags === 'string' ? JSON.parse(post.tags) : []);
                        return tagsArray && tagsArray.length > 0 ? (
                          <div className="flex items-start space-x-2">
                            <TagIcon className="h-4 w-4 text-gray-400 mt-1 flex-shrink-0" />
                            <div className="flex flex-wrap gap-2">
                              {tagsArray.map((tag: string, idx: number) => (
                                <span key={idx} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : null;
                      })()}
                      <Link 
                        to={`/blog/${post.slug}`}
                        className="text-primary hover:text-primary-700 font-bold inline-flex items-center text-sm group"
                      >
                        Read Full Article 
                        <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Categories Section */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-black text-gray-900 mb-8 text-center">
            <span className="text-primary">Explore</span> Topics
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {['Plumbing', 'Construction', 'Electrical', 'Industrial', 'Infrastructure', 'Tank Calibration'].map((category, index) => (
              <button
                key={index}
                className="bg-white p-4 rounded-lg shadow-md hover:shadow-xl hover:bg-primary hover:text-white transition-all duration-300 font-bold text-gray-700 text-center"
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="bg-gradient-to-br from-primary to-primary-700 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-black mb-4">Stay Updated</h2>
          <p className="text-xl mb-8 text-primary-100">
            Get expert insights, project updates, and industry tips delivered directly to your inbox
          </p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-2xl mx-auto" onSubmit={(e) => e.preventDefault()}>
            <input
              type="email"
              placeholder="Enter your email address"
              required
              className="flex-1 px-6 py-4 rounded-lg text-gray-900 focus:outline-none focus:ring-4 focus:ring-white/50"
            />
            <button
              type="submit"
              className="bg-white text-primary px-8 py-4 rounded-lg font-bold hover:bg-primary-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
            >
              Subscribe
            </button>
          </form>
          <p className="text-primary-200 text-sm mt-4">
            Join 500+ professionals receiving our monthly newsletter
          </p>
        </div>
      </section>
    </div>
  );
};

export default Blog;
